/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import {Component, ElementRef, EventEmitter, Output, ViewChild} from '@angular/core';
import {ICellEditorAngularComp} from 'ag-grid-angular';

@Component({
    selector: 'image-editor',
    template: `
        @if ((fileId > 0) || imageFile) {
          @if ((fileId > 0)) {
            <image-with-tooltip [readOnly]="readOnly"
              [imageUrl]="urlBase + '/' + compId + '/' + fileId + '.png'"
              (onRemoveClick)="removeSelectedFile()"/>
          }
          @if (imageFile) {
            <image-with-tooltip [imageUrl]="fileUrl" [readOnly]="readOnly"
              (onRemoveClick)="removeSelectedFile()"/>
          }
        } @else {
          @if (!readOnly) {
            <a title="Upload image" (click)="triggerFileInput()"><span
            class="fa fa-upload"></span></a>
          }
          <input type="file" #fileInput style="display: none;" (change)="onFileSelected($event)" accept="image/*">
        }
        `,
    standalone: false
})
export class ImageCellRenderComponent implements ICellEditorAngularComp {
    @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

    public urlBase: string | null = null;
    public fileId = -1;
    public compId = -1;
    public params: any = null;
    public imageFile: File | null = null;
    public readOnly = true;
    public fileUrl: string | null = null;
    @Output() valueChange = new EventEmitter<any>();

    agInit(params: any): void {
        this.urlBase = params.colDef.cellRendererParams.urlBase;
        this.fileId = params.value;
        const rowId = params.data.id;
        const originalRowId = params.data.originalRowData?.id;
        this.compId = rowId === -1 && originalRowId != null && originalRowId !== -1 ? originalRowId : rowId;
        this.imageFile = params.data['imageFile'];
        if (this.imageFile) {
            this.fileUrl = URL.createObjectURL(this.imageFile);
        }

        this.params = params;
        // Set readOnly property based on the editable function
        if (typeof params.colDef.editable === 'function') {
            this.readOnly = !params.colDef.editable(params);
        } else {
            this.readOnly = params.colDef.editable === false;
        }
    }

    getValue() {
        return {
            fileId: this.fileId,
            imageFile: this.imageFile
        };
    }

    refresh(params: any): boolean {
        return false;
    }

    triggerFileInput() {
        // Use ViewChild to safely trigger the file input
        this.fileInputRef.nativeElement.click();
    }

    onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (file) {
            // Revoke the previous object URL before creating a new one
            this.revokeObjectURLOnFileChange();

            // Clear the previous file ID and set the new file
            this.fileId = -1;
            this.imageFile = file;
            this.fileUrl = URL.createObjectURL(file);
            // a negative -2 is used to indicate a new file (-1 means no file, a positive number means a file exists)
            const newValue = -2;
            this.params.data[this.params.colDef.field] = newValue;
            this.params.data['imageFile'] = file;
            this.valueChange.emit(newValue);
        }
        this.params.attachmentChanged?.();
    }

    removeSelectedFile() {
        // Revoke the previous object URL before creating a new one
        this.revokeObjectURLOnFileChange();
        this.imageFile = null;
        this.fileUrl = null;
        this.fileId = -1;
        this.params.data[this.params.colDef.field] = -1;
        this.params.data['imageFile'] = null;
        this.params.attachmentChanged?.();
    }

    // Utility to clean up object URL
    revokeObjectURLOnFileChange() {
        if (this.fileUrl) {
            URL.revokeObjectURL(this.fileUrl);
        }
    }
}
